import React, { createContext, useContext, useMemo } from "react";

/**
 * Identifies which form a `<DocumentView>` is filling AND which
 * domain entities the fill is happening "on" (its context).
 *
 *   - Identity: exactly one of `formOid` (authenticated) or
 *     `publicSlug` (anonymous public-form). Used by entity-data
 *     resolvers to authorise the request.
 *   - Context: a map keyed by entity kind — Job, Managed Property,
 *     etc. Entity-data pickers can opt into auto-filling /
 *     filtering by these via configForm toggles.
 *
 * Both halves default to empty. Callers outside a fill route (e.g.
 * the editor without form data) leave them blank; entity-data
 * elements fall back to an editor-only placeholder UI in that case.
 */

/** Discrete kinds of context entity a form-fill can carry. */
export const CONTEXT_ENTITY_KINDS = [
  "customer",
  "job",
  "managedProperty",
  "project",
  "calendarEvent",
  "user",
  "contact",
  "asset",
] as const;
export type ContextEntityKind = (typeof CONTEXT_ENTITY_KINDS)[number];

/**
 * A context entry — shaped exactly like the rich-entry snapshot the
 * matching entity picker persists, so the same chip-render code can
 * be reused by the preview panel. Always carries `id` + `name`;
 * extra fields are passthrough.
 */
export interface ContextEntityRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

export type FormContextEntities = {
  [K in ContextEntityKind]: ContextEntityRow | null;
};

const EMPTY_CONTEXT: FormContextEntities = Object.freeze({
  customer: null,
  job: null,
  managedProperty: null,
  project: null,
  calendarEvent: null,
  user: null,
  contact: null,
  asset: null,
}) as FormContextEntities;

/**
 * Build a full context map from a partial one, filling the missing kinds
 * with `null`. Lets a host seed only the kinds it knows (e.g. the current
 * client user) without spelling out the whole shape.
 */
export const makeFormContext = (
  partial?: Partial<FormContextEntities> | null,
): FormContextEntities => ({ ...EMPTY_CONTEXT, ...(partial ?? {}) });

export interface FormFillContextValue {
  formOid: string | null;
  publicSlug: string | null;
  /**
   * The FormDocumentResult.id the live draft is being saved into.
   * Null until startFormDraft has resolved — file-upload elements
   * gate their picker UI on this so they can't kick off an upload
   * before the result row exists.
   */
  formDocumentResultId: string | null;
  /**
   * Anonymous fill identifier. Null in the authenticated path.
   * Threaded into per-result file mutations / queries that need
   * to gate on it.
   */
  secretToken: string | null;
  context: FormContextEntities;
  /**
   * Replace a single context entry. Passing `null` clears the entry.
   * No-op when the provider was rendered without a `context` arg
   * (callers that don't need mutability skip wiring a setter).
   */
  setContextEntity: (
    kind: ContextEntityKind,
    entry: ContextEntityRow | null,
  ) => void;
}

const NOOP_SETTER: FormFillContextValue["setContextEntity"] = () => {};

const FormFillContext = createContext<FormFillContextValue>({
  formOid: null,
  publicSlug: null,
  formDocumentResultId: null,
  secretToken: null,
  context: EMPTY_CONTEXT,
  setContextEntity: NOOP_SETTER,
});

interface FormFillContextProviderProps {
  formOid?: string | null;
  publicSlug?: string | null;
  formDocumentResultId?: string | null;
  secretToken?: string | null;
  /**
   * The current context map. When supplied alongside `onContextChange`,
   * the provider is controlled — the parent owns the state. When
   * omitted, the provider exposes an empty context with a no-op setter
   * (legacy behaviour for fill routes that don't surface a UI for
   * context yet).
   */
  context?: FormContextEntities;
  onContextChange?: (next: FormContextEntities) => void;
  children: React.ReactNode;
}

export const FormFillContextProvider: React.FC<
  FormFillContextProviderProps
> = ({
  formOid = null,
  publicSlug = null,
  formDocumentResultId = null,
  secretToken = null,
  context,
  onContextChange,
  children,
}) => {
  const effectiveContext = context ?? EMPTY_CONTEXT;
  const setContextEntity = useMemo<
    FormFillContextValue["setContextEntity"]
  >(() => {
    if (!context || !onContextChange) return NOOP_SETTER;
    return (kind, entry) => {
      onContextChange({ ...context, [kind]: entry });
    };
  }, [context, onContextChange]);
  const value = useMemo<FormFillContextValue>(
    () => ({
      formOid,
      publicSlug,
      formDocumentResultId,
      secretToken,
      context: effectiveContext,
      setContextEntity,
    }),
    [
      formOid,
      publicSlug,
      formDocumentResultId,
      secretToken,
      effectiveContext,
      setContextEntity,
    ],
  );
  return (
    <FormFillContext.Provider value={value}>
      {children}
    </FormFillContext.Provider>
  );
};

export const useFormFillContext = (): FormFillContextValue =>
  useContext(FormFillContext);
